import { ElementType, HTMLAttributes, ReactNode, forwardRef } from 'react';
import styles from './ScrollArea.module.css';

// THE scroll container for the whole app — Figma "⚠️ Symphony DS" → Scroll bar
// (node 334:278). Never hand-roll `overflow: auto` on a div; see CLAUDE.md →
// "Scrollbars".
//
// A scrollbar cannot exist without the box it scrolls, so the primitive IS that
// box: `<ScrollArea>` scrolls, and the designed bar comes with it. Prop names stay
// MUI-flavoured per the design-system convention, so:
//   axis → which way it scrolls (vertical | horizontal | both)
//   bar  → whether the designed bar is drawn (auto | hidden)
//
// Figma's Position axis (Start / Middle / End) is the knob's own travel and needs
// no prop; its State axis (Default / Hover / Active) is the browser's, mapped onto
// the `--ds-scrollbar-*` tokens in the stylesheet.
//
// Polymorphic + ref-forwarding, because scroll containers get measured: pass
// `component` for a `<pre>` or `<section>`, and a `ref` to read `scrollTop`.
export type ScrollAreaAxis = 'vertical' | 'horizontal' | 'both';
export type ScrollAreaBar = 'auto' | 'hidden';

export interface ScrollAreaProps extends HTMLAttributes<HTMLElement> {
  /** Which way it scrolls. Default `vertical`. */
  axis?: ScrollAreaAxis;
  /** `hidden` still scrolls but draws no bar — a swipeable row, a drawer. */
  bar?: ScrollAreaBar;
  component?: ElementType;
  children?: ReactNode;
}

export const ScrollArea = forwardRef<HTMLElement, ScrollAreaProps>(function ScrollArea(
  { axis = 'vertical', bar = 'auto', component, className, children, ...rest },
  ref,
) {
  const Component = (component ?? 'div') as ElementType;
  const classes = [styles.scroll, styles[axis], bar === 'hidden' && styles.hidden, className].filter(Boolean).join(' ');

  return (
    <Component ref={ref} className={classes} {...rest}>
      {children}
    </Component>
  );
});
